import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { ArrowRight, ChevronDown, Menu, X } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import ThemeToggle from './ThemeToggle'
import { cn } from '@/utils/cn'
import { useScopeTheme } from '@/design-system/island'
import '@/design-system/site.css'
import { BrandMark } from '@/design-system/primitives'
import { PRICING_MENU, PRODUCT_MENU, PRODUCT_MENU_FEATURE } from '@/design-system/products'
import { LEARN_MENU_FEATURES, LEARN_MENU_GROUP, LEARN_MENU_LINKS } from '@/design-system/learn'
import StartFree from '@/components/home/StartFree'
import { useSignupFirst } from '@/hooks/useSignupFirst'

const DemoRequestModal = lazy(() => import('./DemoRequestModal'))
const AgencyApplicationModal = lazy(() => import('./agency/AgencyApplicationModal'))

interface PublicGlobalHeaderProps {
  isSignedIn?: boolean
  accountAction?: AccountAction
  demoSource?: string
  /**
   * Which conversation this page's CTA is for. /agency sells the partner
   * program, so the product wizard's questions and the product Calendly
   * event are both wrong there.
   */
  demoVariant?: 'product' | 'agency'
  /**
   * Strips the header back to the logo alone. For conversion landing pages
   * where nav links, the demo prompt, and sign-in all compete with the one
   * action on the page.
   */
  minimal?: boolean
}

/*
  Products is a menu, not a link, so it is not in this list: it is rendered
  before them. The list is what is left, in the order it was.

  `/features` used to be here. It is gone, and so is the page: nine product
  pages replaced one page carrying six stories, several of which described
  capabilities inaccurately.
*/
export interface AccountAction {
  href: string
  label: string
}
export const DASHBOARD_ACTION: AccountAction = { href: '/dashboard', label: 'Go to Dashboard' }

const publicNavLinks = [
  { href: '/hubspot-aeo', label: 'For HubSpot' },
  { href: '/methodology', label: 'Methodology' },
  { href: '/contact', label: 'Contact' },
]

export default function PublicGlobalHeader({
  isSignedIn = false,
  /* Where the signed-in action goes. The dashboard, unless the account is a
     HubSpot-path sign-up still waiting on its trial, which is sent to its fix. */
  accountAction = DASHBOARD_ACTION,
  demoSource = 'global_header',
  demoVariant = 'product',
  minimal = false,
}: PublicGlobalHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  /*
    One menu at a time. `openMenu` rather than a boolean each, so opening
    one closes the others without a second effect to keep them in step.
  */
  const [openMenu, setOpenMenu] = useState<'products' | 'pricing' | 'learn' | null>(null)
  const isProductsOpen = openMenu === 'products'
  const isPricingOpen = openMenu === 'pricing'
  const isLearnOpen = openMenu === 'learn'
  const [showDemoModal, setShowDemoModal] = useState(false)
  /* Nothing at build time, on purpose: the static pages carry this header as
     built markup, and a theme claimed there is the wrong one for half the
     people who load it. See useScopeTheme. */
  const theme = useScopeTheme()
  const signupFirst = useSignupFirst()
  const productsRef = useRef<HTMLDivElement>(null)
  const pricingRef = useRef<HTMLDivElement>(null)
  const learnRef = useRef<HTMLDivElement>(null)

  /*
    The menu closes on Escape and on a click outside it.

    Both, not either: a menu that only closes when you click its button
    strands itself open over the page the moment somebody's attention moves
    on, and a menu with no Escape traps a keyboard.
  */
  useEffect(() => {
    if (!openMenu) return
    const held = openMenu === 'products' ? productsRef : openMenu === 'pricing' ? pricingRef : learnRef
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenMenu(null)
    }
    const onDown = (event: MouseEvent) => {
      if (!held.current?.contains(event.target as Node)) setOpenMenu(null)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [openMenu])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const closeMobileMenu = () => setIsMobileMenuOpen(false)

  /* The signed-in and signed-out headers ask for different things, but they
     ask in the same shapes, so the two menus are written once. */
  /* Plain anchors throughout, as before. Pricing, Features and Methodology
     are served by Astro, so a router link would never reach them, and the
     signed-in and signed-out targets stay full navigations the way they were. */
  /*
    Signed out, the one primary action is the free account. The demo and
    sign-in step back a weight each, so the row still has one thing to press
    first. With the switch off there is no free account to start, and the row
    is the pair it was.

    The phone menu draws these same actions, stacked, so the free account is
    there too and nothing has to be kept in step between the two. Only their
    order differs there: see `sheetActions`.

    /agency is the exception. That page sells the partner program, and a free
    account is the in-house buyer's flow, so its header keeps the pair
    whatever the switch says.
  */
  const offersFreeAccount = signupFirst && demoVariant !== 'agency'
  const threeButtons = !isSignedIn && offersFreeAccount
  /*
    The three-button state, one element each, so the row and the menu can
    put the same three in different orders.

    `ds-masthead__demo` is how the stylesheet takes the demo out of the wide
    row between 1024px and 1151px, where three buttons do not fit. It is on
    the button only in this state: with two buttons nothing is ever hidden.
  */
  const demoButton = (
    <button
      type="button"
      onClick={() => setShowDemoModal(true)}
      className="ds-btn ds-btn--secondary ds-btn--sm ds-masthead__demo"
    >
      Get a Demo
    </button>
  )
  const signInLink = (
    <a href="/sign-in" className="ds-btn ds-btn--ghost ds-btn--sm">
      Sign In
    </a>
  )
  const startFree = <StartFree placement="header" size="sm" arrow={false} />
  const actions = isSignedIn ? (
    <a href={accountAction.href} className="ds-btn ds-btn--primary ds-btn--sm">
      {accountAction.label}
      <ArrowRight aria-hidden="true" />
    </a>
  ) : offersFreeAccount ? (
    <>
      {demoButton}
      {signInLink}
      {startFree}
    </>
  ) : (
    <>
      <button type="button" onClick={() => setShowDemoModal(true)} className="ds-btn ds-btn--primary ds-btn--sm">
        Get a Demo
      </button>
      <a href="/sign-in" className="ds-btn ds-btn--secondary ds-btn--sm">
        Sign In
      </a>
    </>
  )
  /*
    The menu's actions are a column, and the first thing in a column is the
    thing to press: the free account, then the demo, then sign-in. Reordered
    here in the markup rather than with a CSS `order`, which would leave a
    keyboard and a screen reader walking the buttons in a different order
    from the one drawn. Every other state is the row's own actions.
  */
  const sheetActions = threeButtons ? (
    <>
      {startFree}
      {demoButton}
      {signInLink}
    </>
  ) : (
    actions
  )

  return (
    /* A <header>, not a DsIsland: the scope is carried by hand so the
       landmark role survives. The header used to fly in from y:-100 on every
       load; the page never moves, so all that is left is the edge lifting
       once you scroll under it. */
    <header
      className={cn('ds ds-masthead', isScrolled && 'ds-masthead--lifted')}
      data-ds-theme={theme}
    >
      <div data-testid="global-header-container" className="ds-masthead__in">
        <a href="/" className="ds-masthead__brand" aria-label="SuperSchema">
          <span className="h-[31px] w-[31px]" data-testid="global-header-logo-icon">
            <BrandMark className="h-full w-full" size={31} />
          </span>
          <span className="brand-wordmark text-xl" data-testid="global-header-wordmark" aria-hidden="true">
            <span className="brand-wordmark-super" data-testid="brand-wordmark-super">Super</span>
            <span className="brand-wordmark-schema" data-testid="brand-wordmark-schema">Schema</span>
          </span>
        </a>

        {!minimal && (
          <>
            <div className="ds-masthead__wide">
              <nav className="ds-masthead__nav" aria-label="Primary">
                <div className="ds-mega__hold" ref={productsRef}>
                  <button
                    type="button"
                    className="ds-masthead__link ds-mega__btn"
                    aria-expanded={isProductsOpen}
                    aria-controls="products-menu"
                    onClick={() => setOpenMenu((open) => (open === 'products' ? null : 'products'))}
                  >
                    Products
                    <ChevronDown aria-hidden="true" />
                  </button>

                  {/*
                    Rendered always, hidden by attribute, so the links are in
                    the document for a crawler that does not click. A products
                    menu that only exists after a click is a products menu no
                    AI system ever reads, on a site about being readable.
                  */}
                  <div
                    id="products-menu"
                    className="ds-mega"
                    data-open={isProductsOpen ? 'true' : undefined}
                    hidden={!isProductsOpen}
                  >
                    <div className="ds-mega__in">
                      <a className="ds-mega__feature" href={PRODUCT_MENU_FEATURE.href}>
                        <span className="ds-mega__pic">
                          {/*
                            Not lazy: see the note on the pricing cards below.
                            A lazy image inside a hidden dropdown is never
                            fetched until the dropdown opens.
                          */}
                          {/* Lowercase via a spread, not `fetchPriority`:
                              React 18 does not know the camelCase prop and
                              logs a warning on every page instead of
                              rendering the attribute. Same as ground.tsx. */}
                          <img
                            src={PRODUCT_MENU_FEATURE.photo}
                            alt=""
                            aria-hidden="true"
                            decoding="async"
                            {...{ fetchpriority: 'low' }}
                          />
                        </span>
                        <span className="ds-mega__ftitle">{PRODUCT_MENU_FEATURE.title}</span>
                        <span className="ds-mega__fblurb">{PRODUCT_MENU_FEATURE.blurb}</span>
                        <span className="ds-mega__flink">
                          {PRODUCT_MENU_FEATURE.cta}
                          <ArrowRight aria-hidden="true" />
                        </span>
                      </a>

                      <div className="ds-mega__cols">
                        {PRODUCT_MENU.map((group) => (
                          <div key={group.title} className="ds-mega__col">
                            {/*
                              A sentence, not an uppercase eyebrow. The
                              reference this menu is drawn from sets these as
                              letter-spaced capitals, and this system does not
                              have eyebrows.
                            */}
                            <p className="ds-mega__group">{group.title}</p>
                            {group.items.map((item) => (
                              <a key={item.slug} className="ds-mega__item" href={`/products/${item.slug}`}>
                                <span className="ds-mega__name">{item.name}</span>
                                <span className="ds-mega__blurb">{item.blurb}</span>
                              </a>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="ds-mega__foot">
                      <a href="/products" className="ds-link">
                        See all products
                        <ArrowRight aria-hidden="true" />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="ds-mega__hold" ref={pricingRef}>
                  <button
                    type="button"
                    className="ds-masthead__link ds-mega__btn"
                    aria-expanded={isPricingOpen}
                    aria-controls="pricing-menu"
                    onClick={() => setOpenMenu((open) => (open === 'pricing' ? null : 'pricing'))}
                  >
                    Pricing
                    <ChevronDown aria-hidden="true" />
                  </button>

                  {/*
                    Rendered always and hidden by attribute, like the products
                    menu: both destinations stay in the document for a crawler
                    that never clicks. /agency is reachable nowhere else, so if
                    this menu only existed after a click, that page would be
                    unlinked as far as any AI system is concerned.
                  */}
                  <div
                    id="pricing-menu"
                    className="ds-mega ds-mega--trio"
                    data-open={isPricingOpen ? 'true' : undefined}
                    hidden={!isPricingOpen}
                  >
                    <div className="ds-mega__in">
                      {PRICING_MENU.map((card) => (
                        <a key={card.href} className="ds-mega__feature" href={card.href}>
                          <span className="ds-mega__pic">
                            {/* Real alt: the category is written into the
                                picture and nowhere else in the card. */}
                            {/*
                            Not lazy, deliberately.

                            These sit inside a `hidden` dropdown, so a lazy
                            image has zero size, never intersects anything, and
                            is not fetched at all until the menu opens. Measured
                            on production: zero of the three had loaded after
                            four seconds on the page, and each one then arrived
                            only on the first open. That is the pause you see.

                            Fetched with the page instead, at low priority so
                            they queue behind anything that paints, which makes
                            them already there when the menu opens.
                          */}
                          <img
                              src={card.photo}
                              alt={card.alt}
                              decoding="async"
                              {...{ fetchpriority: 'low' }}
                            />
                          </span>
                          <span className="ds-mega__ftitle">{card.title}</span>
                          <span className="ds-mega__fblurb">{card.blurb}</span>
                          <span className="ds-mega__flink">
                            {card.cta}
                            <ArrowRight aria-hidden="true" />
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="ds-mega__hold" ref={learnRef}>
                  <button
                    type="button"
                    className="ds-masthead__link ds-mega__btn"
                    aria-expanded={isLearnOpen}
                    aria-controls="learn-menu"
                    onClick={() => setOpenMenu((open) => (open === 'learn' ? null : 'learn'))}
                  >
                    Learn
                    <ChevronDown aria-hidden="true" />
                  </button>

                  {/*
                    Rendered always and hidden by attribute, like the other
                    two. The course used to be reachable from nowhere in the
                    nav; a menu that only existed after a click would leave it
                    unlinked as far as any AI system is concerned.
                  */}
                  <div
                    id="learn-menu"
                    className="ds-mega ds-mega--learn"
                    data-open={isLearnOpen ? 'true' : undefined}
                    hidden={!isLearnOpen}
                  >
                    <div className="ds-mega__in">
                      {LEARN_MENU_FEATURES.map((card) => (
                        <a key={card.href} className="ds-mega__feature" href={card.href}>
                          <span className="ds-mega__pic">
                            {/* Not lazy, for the reason the pricing cards give:
                                a lazy image in a hidden dropdown is never fetched
                                until the dropdown opens. */}
                            <img
                              src={card.photo}
                              alt=""
                              aria-hidden="true"
                              decoding="async"
                              {...{ fetchpriority: 'low' }}
                            />
                          </span>
                          <span className="ds-mega__ftitle">{card.title}</span>
                          <span className="ds-mega__fblurb">{card.blurb}</span>
                          <span className="ds-mega__flink">
                            {card.cta}
                            <ArrowRight aria-hidden="true" />
                          </span>
                        </a>
                      ))}

                      <div className="ds-mega__cols">
                        <div className="ds-mega__col">
                          <p className="ds-mega__group">{LEARN_MENU_GROUP}</p>
                          {LEARN_MENU_LINKS.map((item) => (
                            <a key={item.href} className="ds-mega__item" href={item.href}>
                              <span className="ds-mega__name">{item.name}</span>
                              <span className="ds-mega__blurb">{item.blurb}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {publicNavLinks.map((link) => (
                  <a key={link.href} href={link.href} className="ds-masthead__link">
                    {link.label}
                  </a>
                ))}
              </nav>
              <div className="ds-masthead__acts">
                <ThemeToggle />
                {actions}
              </div>
            </div>

            <div className="ds-masthead__phone">
              <ThemeToggle />
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen((open) => !open)}
                className="ds-masthead__burger"
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
              </button>
            </div>
          </>
        )}
      </div>

      {/* AnimatePresence only animates children it owns, so the scope stays
          on the motion element rather than wrapping it. */}
      <AnimatePresence>
        {!minimal && isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="ds ds-masthead__sheet"
            data-ds-theme={theme}
          >
            <div className="ds-masthead__sheetin">
              {/* On a phone the mega menu is just the list, flattened. A
                  hover panel has nothing to hover. */}
              {PRODUCT_MENU.map((group) => (
                <div key={group.title} className="ds-masthead__sheetgroup">
                  <p className="ds-masthead__sheethead">{group.title}</p>
                  {group.items.map((item) => (
                    <a
                      key={item.slug}
                      href={`/products/${item.slug}`}
                      onClick={closeMobileMenu}
                      className="ds-masthead__sheetlink"
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              ))}
              {/*
                No mega menu on a phone: the three pricing paths are rows.

                Labelled from `short` rather than from the title, which
                lower-cased produced rows reading "Pricing improve your
                company's visibility in ai search".
              */}
              {PRICING_MENU.map((card) => (
                <a
                  key={card.href}
                  href={card.href}
                  onClick={closeMobileMenu}
                  className="ds-masthead__sheetlink"
                >
                  {`Pricing: ${card.short}`}
                </a>
              ))}
              {/*
                Learn, flattened the way the product groups are. Methodology
                is already one of the plain rows below, so it is left out
                here rather than listed twice in one sheet.
              */}
              <div className="ds-masthead__sheetgroup">
                <p className="ds-masthead__sheethead">Learn</p>
                {LEARN_MENU_FEATURES.map((card) => (
                  <a key={card.href} href={card.href} onClick={closeMobileMenu} className="ds-masthead__sheetlink">
                    {card.short}
                  </a>
                ))}
                {LEARN_MENU_LINKS.filter((item) => !publicNavLinks.some((link) => link.href === item.href)).map((item) => (
                  <a key={item.href} href={item.href} onClick={closeMobileMenu} className="ds-masthead__sheetlink">
                    {item.name}
                  </a>
                ))}
              </div>
              {publicNavLinks.map((link) => (
                <a key={link.href} href={link.href} onClick={closeMobileMenu} className="ds-masthead__sheetlink">
                  {link.label}
                </a>
              ))}
              <div className="ds-masthead__sheetacts" onClick={closeMobileMenu}>
                {sheetActions}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {showDemoModal && (
        <Suspense fallback={null}>
          {demoVariant === 'agency' ? (
            <AgencyApplicationModal
              isOpen={showDemoModal}
              onClose={() => setShowDemoModal(false)}
              source={demoSource}
            />
          ) : (
            <DemoRequestModal
              isOpen={showDemoModal}
              onClose={() => setShowDemoModal(false)}
              source={demoSource}
            />
          )}
        </Suspense>
      )}
    </header>
  )
}
